/**
 * The pure half of the map view (nobgp-router#1934): where the graph's nodes go on a map.
 *
 * The map draws the SAME graph as the graph view: account → network → location → node →
 * service. The one difference is where things are (Benoit, 2026-10-01):
 *
 * - a LOCATION node is pinned at its real place on the map, with no label: the map names it;
 * - its NODES have SEATS on a ring round it, as the graph spreads them round it, so that several
 *   nodes behind one address can each be selected. A node is held near its seat, not pinned to
 *   it, so that it can give way to a service (`mapCore.ts`, Benoit 2026-10-03);
 * - the ACCOUNT (the home icon) is pinned at the viewer's place. A location that is exactly
 *   there is NOT drawn: the home icon is its location node, and its nodes go round it. With no
 *   node there, there is no home icon, and there is never more than one (`planGraph`);
 * - a NETWORK is not pinned: the simulation puts it between the locations it links to;
 * - services are not pinned either, and hang off their node as in the graph;
 * - a CLOUD location (noBGP cloud, Grok Bot) is not on the earth: it FLOATS near the locations of
 *   its networks, clear of their rings (`planGraph`, `layoutView`).
 *
 * ⚠ This module imports nothing that imports, for the reason `update.ts` and `fit.ts` do not: `mapCore.ts`
 * needs a DOM and a WebGL canvas, so a test could not reach these decisions there.
 */

import { mergeArcs } from './networkHues.ts'

export interface Place {
  lng: number
  lat: number
}

/** The two fields of a `node_metadata` row, or of `whoami`'s `location`, that give a place. */
export interface Located {
  latitude?: number | null
  longitude?: number | null
}

/** Where a pinned node is drawn, in SCREEN pixels, for the map as it is shown now. */
export interface Spot {
  x: number
  y: number
  /**
   * For a node on a ring: its bearing from the middle of the ring, in radians. The label goes
   * on that side, so the names round a ring point outward and do not lie on each other.
   */
  angle?: number
  /** A location or a node with no place of its own, drawn at the viewer's place. */
  atViewer?: boolean
  /** A node on a ring that has another ring outside it. Its name is shown only when it is lit. */
  inner?: boolean
  /**
   * How much larger than at its first zoom this location's group is drawn: its icon, its ring and
   * every badge on it (`layoutView`'s `scale`). 1 when not given.
   */
  scale?: number
}

const distanceOf = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y)

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

/**
 * A place from a latitude and a longitude, or null when the pair is not one.
 *
 * ⚠ 0 is a real latitude and a real longitude, so the test is "is a number in range" and never
 * "is truthy". A row with only one of the two is not a place.
 */
export function placeOf(located: Located | null | undefined): Place | null {
  if (!located) return null
  const { latitude, longitude } = located
  if (!finite(latitude) || !finite(longitude)) return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null
  return { lng: longitude, lat: latitude }
}

/** The gap between two neighbours on a ring, in pixels. A node's glyph is 20 px across. */
const RING_SPACING = 36
/**
 * The smallest ring of nodes round a location.
 *
 * ⚠ 44, and it was 30: a location is the data center icon now (26 px across), and a ring can be
 * wider because the reader can close it with a click (Benoit, 2026-10-02).
 */
export const NODE_RING_MIN_RADIUS = 44
/** The smallest ring round the user's own icon, which is 50 px across. */
export const VIEWER_RING_MIN_RADIUS = 50
/** The space kept between two rings that are side by side. */
const RING_GAP = 26
/** Half a node's glyph: how far a ring's nodes reach past the ring itself. */
const NODE_REACH = 12

/** The radius of a ring that holds `count` things `RING_SPACING` apart, and is at least `min`. */
export function ringRadius(count: number, min: number): number {
  return Math.max(min, (count * RING_SPACING) / (2 * Math.PI))
}

/**
 * `count` points on a ring of the given radius, with the bearing of each.
 *
 * ⚠ The first is at `start` (the TOP unless the caller says otherwise) and the rest follow
 * clockwise, so a given count always draws the same ring.
 */
export function ringOffsets(
  count: number,
  radius: number,
  start = -Math.PI / 2
): { dx: number; dy: number; angle: number }[] {
  return Array.from({ length: count }, (_, i) => {
    const angle = start + (i / count) * 2 * Math.PI
    return { dx: radius * Math.cos(angle), dy: radius * Math.sin(angle), angle }
  })
}

/** More nodes than this on one ring go on rings inside each other. See `ringSeats`. */
const RING_SINGLE_MAX = 24
/** The innermost of several rings, and the distance from one ring to the next. */
const RING_FIRST = 60
const RING_STEP = 44

/**
 * The rings that hold `count` nodes: the radius of each and how many nodes it holds, inner first.
 *
 * Up to `RING_SINGLE_MAX` nodes are on ONE ring, as the map always drew them. More than that go on
 * rings inside each other (nobgp/app#449): one ring of 72 nodes is 825 px across, which no window
 * holds, and four rings are 384 px. Each ring gets its share of the nodes by its length, so the
 * outer ring is not left with two.
 */
function ringPlan(count: number, min: number): { radius: number; holds: number }[] {
  if (count <= 0) return []
  if (count <= RING_SINGLE_MAX) return [{ radius: ringRadius(count, min), holds: count }]
  const rings: { radius: number; room: number }[] = []
  let room = 0
  for (let radius = Math.max(min, RING_FIRST); room < count; radius += RING_STEP) {
    const holds = Math.floor((2 * Math.PI * radius) / RING_SPACING)
    rings.push({ radius, room: holds })
    room += holds
  }
  let left = count
  return rings.map((ring, i) => {
    const holds = i === rings.length - 1 ? left : Math.min(left, Math.round((count * ring.room) / room))
    left -= holds
    return { radius: ring.radius, holds }
  })
}

/** How far the rings of `count` nodes reach from their middle. 0 with no node. */
export function ringReach(count: number, min: number): number {
  const rings = ringPlan(count, min)
  return rings.length ? rings[rings.length - 1].radius : 0
}

/**
 * A seat for each of `count` nodes round one middle, in order: the inner ring first. A seat on a
 * ring that has another ring outside it is `inner`.
 */
export function ringSeats(count: number, min: number): { dx: number; dy: number; angle: number; inner?: boolean }[] {
  const rings = ringPlan(count, min)
  return rings.flatMap((ring, i) =>
    ringOffsets(ring.holds, ring.radius).map(seat => (i < rings.length - 1 ? { ...seat, inner: true } : seat))
  )
}

/** Two places are one point on the map when they are the same to four decimals (about 11 m). */
const pointKey = (place: Place) => `${place.lat.toFixed(4)},${place.lng.toFixed(4)}`

const endId = (end: string | GraphNode) => (typeof end === 'string' ? end : end.id)

const byId = (a: { id: string }, b: { id: string }) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

/**
 * Names in alphabetical order, upper and lower case alike, then by id (Benoit, 2026-10-03: "organize
 * the node alphabetically (case insensitive) clockwise from the 12 o'clock position"). The seats
 * of a ring start at the top and go clockwise (`ringOffsets`), so this order is the ring's.
 */
export function byName(a: { id: string; label?: string }, b: { id: string; label?: string }): number {
  return (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base', numeric: true }) || byId(a, b)
}

/** One location node of the plan, with the nodes on its ring. */
export interface PlannedHub {
  id: string
  place: Place
  /** None of its nodes has a place: it is at the viewer's. */
  atViewer: boolean
  /** The ids of its nodes, in ring order. */
  nodes: string[]
}

/**
 * A cloud location (`GraphNode.cloudPlace`): every node of one cloud, in every network, round ONE icon
 * that is not at a place on the earth. See `planGraph`.
 */
export interface PlannedFloat {
  id: string
  /** The ids of its nodes, in ring order. */
  nodes: string[]
  /** The drawn locations, and the home icon, that share a network with it: it floats near them. */
  near: string[]
}

/** What the map draws, and where on the EARTH each pinned part of it is. See `planGraph`. */
export interface GraphPlan {
  /**
   * The home icon, at the viewer's place, with the nodes that go straight round it. Null when no
   * node is there. `id` is the one account that is drawn.
   */
  account: { id: string; place: Place; around: { id: string; atViewer: boolean }[] } | null
  hubs: PlannedHub[]
  /** The cloud locations. Each is drawn at a place that `layoutView` gives it on the screen. */
  floats: PlannedFloat[]
  nodes: GraphNode[]
  links: GraphLink[]
  /** How many nodes have no place and are not drawn. */
  unplaced: number
}

/**
 * The graph as the map draws it, and the place of each pinned part. The first of two steps:
 * this one knows the earth, and `layoutPlan` knows the screen.
 *
 * A LOCATION's place is the mean of its nodes' coordinates. The graph groups nodes into a
 * location by the NAME of the place ("Petaluma, CA"), and two addresses in one city can have
 * slightly different coordinates, so one name can hold more than one point.
 *
 * A location none of whose nodes has coordinates (the graph calls it "Unknown") is at the
 * VIEWER's place. With no viewer place it is not drawn, and neither are its nodes and what hangs
 * off them.
 *
 * ⚠⚠ THE HOME ICON IS A PLACE WITH NODES, AND THERE IS ONE (Benoit, 2026-10-03: "these should not
 * show if there are no nodes there" and "why two?"). The graph has one account for each owner of
 * a network, and one for the viewer. The map drew each of them at the viewer's place, so the fleet
 * of another member showed two home icons where it has no node. Now:
 *
 * - with no node at the viewer's place, no account is drawn. A network is then held by its
 *   locations, and a network with no node is not drawn, as with no viewer place;
 * - with nodes there, the VIEWER's account (`self`) is the home icon, or the first one when the
 *   caller does not know it. The others are not drawn. ⚠ Not always the first: the ids are user ids, so "first" was
 *   the account of another member for half the viewers, and the icon was lit when THAT account
 *   was selected.
 *
 * ⚠⚠ NO LINE FROM THE USER TO A NETWORK (Benoit, 2026-10-09). A network links to its locations,
 * and the home icon is one of them. The account's own link is drawn only for a network that no
 * drawn location holds, from the home icon, so that the network is not left with nothing.
 *
 * ⚠⚠ A LOCATION EXACTLY AT THE VIEWER'S PLACE IS NOT DRAWN (Benoit, 2026-10-01). The user icon
 * is its location node: its nodes go on the ring round the icon and link to it, and the network's
 * link to that location goes to the icon. This is the usual case and not an edge: the
 * viewer is behind the same address as the nodes beside them. A node there that has no place of
 * its own is marked `atViewer`.
 *
 * ⚠ `merged` is the caller's, and it makes a node's link to the user icon the SAME OBJECT on
 * every draw. d3 keeps a link's resolved ends on the object; a new object each time would
 * restart every line's fade.
 *
 * ⚠⚠ A CLOUD LOCATION IS NOT ON THE EARTH (Benoit, 2026-10-20: "a virtual (and floating/self
 * arranging) location for all noBGP cloud node and one for the Grokbot nodes. So they do not appear
 * in there actual true location, but instead are grouped by cloud type"). The adapter makes one
 * location for each cloud, for all networks (`GraphNode.cloudPlace`). It is a FLOAT here and not a hub:
 * it has no place, it never stands in for the home icon, and `layoutView` puts it near the drawn
 * locations of its networks (`near`). The place of its nodes is not read.
 *
 * ⚠ Every ring is ordered by NAME (`byName`, Benoit 2026-10-03) and not by the input: the input
 * follows the store, which changes when a node reconnects, and a node that changed seats on every
 * refresh could not be followed.
 */
export function planGraph(
  nodes: GraphNode[],
  links: GraphLink[],
  metadata: Map<string, Located>,
  viewer: Place | null,
  merged: Map<string, GraphLink> = new Map(),
  /** The id of the viewer's own account: the one that is drawn as the home icon. */
  self?: string
): GraphPlan {
  const nodeById = new Map(nodes.map(node => [node.id, node]))
  const hasPlace = (node: GraphNode) => !!(node.modelNodeId && placeOf(metadata.get(node.modelNodeId)))

  // location → its nodes, from the links the adapter made.
  const members = new Map<string, GraphNode[]>()
  links.forEach(link => {
    if (link.sourceType !== 'location') return
    const node = nodeById.get(endId(link.target))
    if (!node) return
    const list = members.get(endId(link.source)) ?? []
    list.push(node)
    members.set(endId(link.source), list)
  })

  const gone = new Set<string>()
  let unplaced = 0

  const accounts = nodes.filter(node => node.type === 'account').sort(byId)
  const atHome: NonNullable<GraphPlan['account']> | null =
    viewer && accounts.length
      ? { id: (accounts.find(one => one.id === self) ?? accounts[0]).id, place: viewer, around: [] }
      : null
  const viewerPoint = atHome ? pointKey(atHome.place) : null

  // location id → the account that stands in for it.
  const standIn = new Map<string, string>()
  const hubs: PlannedHub[] = []
  const floats: PlannedFloat[] = []

  nodes
    .filter(node => node.type === 'location')
    .sort(byId)
    .forEach(location => {
      const own = (members.get(location.id) ?? []).sort(byName)
      if (location.cloudPlace) {
        // ⚠ A cloud with no node is not drawn. A search keeps it by its name ("Grok Bot") and drops
        // its nodes: with no ring it had no spot, and it went to the corner of the window.
        if (!own.length) gone.add(location.id)
        else floats.push({ id: location.id, nodes: own.map(node => node.id), near: [] })
        return
      }
      const places = own
        .map(node => (node.modelNodeId ? placeOf(metadata.get(node.modelNodeId)) : null))
        .filter((place): place is Place => !!place)
      const mean = places.length
        ? {
            lat: places.reduce((sum, place) => sum + place.lat, 0) / places.length,
            lng: places.reduce((sum, place) => sum + place.lng, 0) / places.length,
          }
        : null
      const place = mean ?? viewer
      if (!place) {
        gone.add(location.id)
        own.forEach(node => gone.add(node.id))
        unplaced += own.length
        return
      }
      if (atHome && pointKey(place) === viewerPoint) {
        // The home icon is this location's node.
        standIn.set(location.id, atHome.id)
        gone.add(location.id)
        own.forEach(node => atHome.around.push({ id: node.id, atViewer: !hasPlace(node) }))
        return
      }
      hubs.push({ id: location.id, place, atViewer: !mean, nodes: own.map(node => node.id) })
    })
  // The home icon is drawn only with nodes round it, and it is the only account that is drawn.
  const account = atHome?.around.length ? atHome : null
  const home = account?.id
  accounts.forEach(one => {
    if (one.id !== home) gone.add(one.id)
  })
  const names = new Map(nodes.map(node => [node.id, node.label]))
  account?.around.sort((a, b) => byName({ id: a.id, label: names.get(a.id) }, { id: b.id, label: names.get(b.id) }))

  // ⚠⚠ ON THE MAP A NETWORK LINKS TO ITS LOCATIONS, AND NOT TO THE USER (Benoit, 2026-10-09: "the
  // line from the user to the network is not needed here"). The home icon is a location, so the
  // line between it and a network is the network's link to THAT location, with its state:
  // `connected` while a node there has a session. It was the account's link before.
  // ⚠ `connected` is the data. The map draws every line of a network dotted: see `solidLink`.
  // ⚠ The account's link stays for a network that no drawn location holds (it has no node, or
  // none that is shown): the home icon then holds it, and it is the only line the network has.
  const drawn = new Set([...standIn.keys(), ...hubs.map(hub => hub.id), ...floats.map(float => float.id)])
  const placed = new Set(
    links.filter(link => link.sourceType === 'network' && drawn.has(endId(link.target))).map(link => endId(link.source))
  )
  const fromUser = (link: GraphLink) => link.sourceType === 'account' && placed.has(endId(link.target))

  // A float is near the drawn locations of its networks: the home icon for one that it stands in for.
  const onEarth = new Set(hubs.map(hub => hub.id))
  floats.forEach(float => {
    const networks = new Set(
      links.filter(link => link.sourceType === 'network' && endId(link.target) === float.id).map(link => endId(link.source))
    )
    const near = new Set<string>()
    links.forEach(link => {
      if (link.sourceType !== 'network' || !networks.has(endId(link.source))) return
      const target = endId(link.target)
      if (onEarth.has(target)) near.add(target)
      else if (home && standIn.get(target) === home) near.add(home)
    })
    float.near = Array.from(near).sort()
  })

  // A node of a location the home icon stands in for links to the icon, and so does a network of
  // that location. A network that only an account that is not drawn holds links to the icon too.
  // The same object on every draw: see `merged`.
  const moved = new Map<string, { source: string; target: string; all: GraphLink[] }>()
  links.forEach(link => {
    const from = endId(link.source)
    const target = endId(link.target)
    let ends: { source: string; target: string } | undefined
    if (link.sourceType === 'location') {
      const to = standIn.get(from)
      if (to) ends = { source: to, target }
    } else if (link.sourceType === 'network') {
      // ⚠ Only to the icon that is DRAWN: with no node round it the home icon is not, and a link
      // to it would keep a network that nothing holds.
      const to = standIn.get(target)
      if (to && to === home) ends = { source: from, target: to }
    } else if (link.sourceType === 'account' && home && from !== home && !fromUser(link)) {
      ends = { source: home, target }
    }
    if (!ends) return
    const id = `${ends.source}>${ends.target}`
    const entry = moved.get(id) ?? { ...ends, all: [] }
    entry.all.push(link)
    moved.set(id, entry)
  })
  const relinked: GraphLink[] = []
  moved.forEach((entry, id) => {
    // ⚠ One link, as it is. Several (two locations of one network at the viewer's place): one
    // line with the states of all of them, as `viewGraph` does for an icon.
    const [first] = entry.all
    const fields =
      entry.all.length === 1
        ? {
            connected: first.connected,
            streaming: first.streaming,
            hidden: first.hidden,
            active: first.active,
            rxBytes: first.rxBytes,
            txBytes: first.txBytes,
            targetType: first.targetType,
            hue: first.hue,
          }
        : {
            connected: entry.all.some(link => link.connected),
            streaming: entry.all.some(link => link.streaming),
            hidden: entry.all.every(link => link.hidden),
            active: entry.all.some(link => link.active),
            rxBytes: entry.all.reduce((sum, link) => sum + (link.rxBytes ?? 0), 0),
            txBytes: entry.all.reduce((sum, link) => sum + (link.txBytes ?? 0), 0),
            targetType: first.targetType,
            hue: entry.all.find(link => link.hue !== undefined)?.hue,
          }
    const existing = merged.get(id)
    if (existing) {
      const changed = (Object.keys(fields) as (keyof typeof fields)[]).some(k => existing[k] !== fields[k])
      Object.assign(existing, fields, { stale: changed })
      relinked.push(existing)
      return
    }
    const created: GraphLink = { id, source: entry.source, target: entry.target, sourceType: first.sourceType, stale: false, ...fields }
    merged.set(id, created)
    relinked.push(created)
  })
  Array.from(merged.keys()).forEach(id => {
    if (!moved.has(id)) merged.delete(id)
  })

  if (!gone.size) return { account, hubs, floats, nodes, links, unplaced }

  // Drop what is not drawn, with what hangs off it. A service goes with its node. A network stays:
  // it is a real network.
  const keptLinks = [
    ...links.filter(link => !gone.has(endId(link.source)) && !gone.has(endId(link.target)) && !fromUser(link)),
    ...relinked,
  ]
  const reached = new Set(keptLinks.map(link => endId(link.target)))
  const held = new Set(keptLinks.flatMap(link => [endId(link.source), endId(link.target)]))
  const keptNodes = nodes.filter(node => {
    if (gone.has(node.id)) return false
    if (node.type === 'service') return reached.has(node.id)
    // ⚠ A network is not pinned: what it links to holds it. With no drawn location and no user
    // icon, nothing holds it, and the simulation leaves it at the corner of the window.
    if (node.type === 'network') return held.has(node.id)
    return true
  })
  return { account, hubs, floats, nodes: keptNodes, links: keptLinks, unplaced }
}

/**
 * Where each pinned node is drawn, in pixels, for the map as it is shown NOW. The second step:
 * `project` turns a place into a point on the screen at the present zoom and position.
 *
 * A location node is on its place and its nodes are on a ring round it. That is all there is to
 * it while every location has room.
 *
 * ⚠⚠ LOCATIONS THAT ARE CLOSE ON THE SCREEN ARE SPREAD, AND "CLOSE" DEPENDS ON THE ZOOM. San
 * Francisco and San Jose are 70 km apart: two pixels at the scale of the world, where their
 * rings would be one heap in which no node can be selected. Locations whose rings would touch
 * are moved onto a ring round their common middle, each on the side it really is, far enough
 * apart that the rings clear. Zoom in and they separate and go back to their places. The same
 * rule spreads two locations on ONE point (a city in two networks) and keeps a location off
 * the user icon.
 *
 * ⚠ One pass. A group that was spread can reach a neighbour it did not reach before; the map
 * accepts that rather than moving things a second time, further from where they are.
 *
 * ⚠ Round the user icon, two runs of locations that were each widened can meet across the cut (see
 * the cut below). The map accepts that for the same reason.
 */
export function layoutPlan(plan: GraphPlan, project: (place: Place) => { x: number; y: number }): Map<string, Spot> {
  return layoutView(plan, project).spots
}

/** A group of locations that the map draws as ONE data center icon. See `layoutView`. */
export interface Cluster {
  id: string
  /** Where the icon is, in screen pixels. */
  x: number
  y: number
  /** The middle of its nodes on the earth. A click moves the map there. */
  place: Place
  /** The location nodes it stands for, and their nodes. None of them is drawn. */
  hubs: string[]
  nodes: string[]
  count: number
  /**
   * The user icon that stands for this group, when the viewer's own location is in it. The map
   * then draws NO icon of its own for the group: the user icon is the location node of that place
   * (`planGraph`), so it carries the count. `x` and `y` are the user icon's.
   */
  account?: string
  /**
   * The reader closed this location with a click, and it has room to be drawn open. A click
   * opens it again where it is. Without this, a click has to zoom the map in.
   */
  folded?: boolean
  /** A cloud location that the reader closed (`PlannedFloat`): its icon is the cloud's. */
  cloudPlace?: CloudPlace
}

/** A group with more nodes than this can be a data center icon. With fewer it is always rings. */
export const CLUSTER_MIN_NODES = 8
/**
 * A location that the spread moves further than this from its place makes its group too busy.
 *
 * ⚠⚠ HALF A PIXEL, AND IT WAS 16 (Benoit, 2026-10-03: "the location appears to move when
 * zooming"). With 16, for about half a zoom step before a group closed, each of its locations was
 * drawn up to 16 px from its place, and the distance changed with the zoom. An open location is
 * now ON its place. Two locations on one point are still side by side round it.
 *
 * ⚠ Not within half a zoom step of the last zoom: there every group is open, so that each node
 * can be reached, and close places are still spread (`layoutView`).
 */
export const CLUSTER_MAX_SHIFT = 0.5
/** A group whose drawing covers more ground than this, from its middle, is too busy. */
export const CLUSTER_MAX_KM = 200
/** Half the data center icon with its count, and the space kept between two icons. Closer icons are one icon. */
const CLUSTER_REACH = 18
const CLUSTER_GAP = 10

const EARTH_METERS = 40_075_016.686
/** The map's tiles are 512 px, so the world is `512 * 2^zoom` px wide. */
const worldSize = (zoom: number) => 512 * 2 ** zoom

/** How many meters of ground one pixel is, at a latitude and a zoom of the map. */
export function metersPerPixel(lat: number, zoom: number): number {
  return (EARTH_METERS * Math.cos((lat * Math.PI) / 180)) / worldSize(zoom)
}

/**
 * The map's projection at a zoom, with no map: a place to a point in pixels of the whole world.
 * `openZoom` uses it to ask what the layout would be at a zoom the map is not at.
 */
export function mercator(zoom: number): (place: Place) => { x: number; y: number } {
  const size = worldSize(zoom)
  return place => {
    const sin = Math.min(0.9999, Math.max(-0.9999, Math.sin((place.lat * Math.PI) / 180)))
    return {
      x: ((place.lng + 180) / 360) * size,
      y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size,
    }
  }
}

/**
 * `layoutPlan`, and which groups of locations are too busy to draw open at this zoom.
 *
 * ⚠⚠ A GROUP THAT IS TOO BUSY IS ONE DATA CENTER ICON (Benoit, 2026-10-02, nobgp/app#449). The
 * spread above keeps close rings apart by MOVING a location, and with many nodes that is a mess:
 * zoomed out, the ring of San Jose was drawn near Fresno, 200 km from San Jose. "The location
 * points did not position themselves correctly." So a group of more than `CLUSTER_MIN_NODES`
 * nodes is drawn as one icon, with the count of its nodes, when either is true at this zoom:
 *
 * - the spread moves one of its locations more than `CLUSTER_MAX_SHIFT` px from its place, or
 * - what it draws reaches more than `CLUSTER_MAX_KM` of ground from its middle. A ring is a fixed
 *   size in pixels, so the further out the map is, the more ground it covers.
 *
 * Zoom in and the group is drawn open again; zoom out and it closes.
 *
 * ⚠ The reader can also close a location that has room, with a click on it (`folded`). It stays
 * closed at every zoom until a click opens it.
 *
 * ⚠⚠ ONE BUSY GROUP CLOSES THEM ALL (Benoit, 2026-10-02). If one group is busy at this zoom, every
 * location is an icon: the wide view is all icons. And a group of any size is busy when the spread
 * MOVES a place: two locations of 1 node each were drawn in Norway and in Tunisia. The size of a
 * group counts only for the ground it covers: a lone location of `CLUSTER_MIN_NODES` nodes or
 * fewer is a ring at every zoom, while nothing else on the map is busy.
 *
 * ⚠ Within half a zoom step of `lastZoom` (the furthest the map zooms in) every group is open, so
 * each node can be reached. With no `zoom` nothing is a data center: that is `layoutPlan`.
 *
 * ⚠ Each location of a busy group is its OWN icon, at its real place. Icons that would touch are
 * one icon, at the middle of their nodes by count: zoom out more and two cities become one icon.
 * The user icon keeps its place and IS the icon of its own place: the nodes that go round it, and
 * a location whose icon would touch it, are counted on the user icon (`Cluster.account`).
 */
export function layoutView(
  plan: GraphPlan,
  project: (place: Place) => { x: number; y: number },
  zoom = Infinity,
  lastZoom = Infinity,
  folded: ReadonlySet<string> = new Set(),
  fit: RingFits = NO_FIT,
  /**
   * How much larger each location's group is drawn, by the id of its location or of the user icon
   * (`groupScale`). Its ring, its seats and its reach grow with it, round its middle.
   */
  scale: ReadonlyMap<string, number> = new Map(),
  /**
   * The window, clear of the side menu: a cloud location is kept in it, and with nothing on the
   * earth drawn it is at its middle (`placeFloats`). None: no edge, and the middle is (0, 0).
   */
  frame?: Frame,
  /**
   * A point for each cloud location that has one, by its id (`cloud:grok`): an Owner or Admin put it
   * there (`org_cloud_places`, nobgp-router#1976), or the reader is dragging it. It is drawn there,
   * and the other clouds keep clear of it.
   */
  pinned: ReadonlyMap<string, Place> = new Map(),
  /**
   * Where each cloud location was drawn the last time, by its id, as a place on the earth
   * (`placeFloats`). A cloud that is still in the window, clear of everything, stays there.
   */
  kept: ReadonlyMap<string, Place> = new Map()
): { spots: Map<string, Spot>; clusters: Cluster[] } {
  const viewerId = plan.account?.id ?? ''
  const scaleOf = (id: string) => scale.get(id) ?? 1
  // A spot carries its scale only when it is not 1: a group at its first size is drawn as it was.
  const grown = (grow: number) => (grow !== 1 ? { scale: grow } : {})
  const viewerMin = Math.max(VIEWER_RING_MIN_RADIUS, fit.min.get(viewerId) ?? 0)
  const hubMin = (id: string) => Math.max(NODE_RING_MIN_RADIUS, fit.min.get(id) ?? 0)
  const seatOut = fit.out
  // ⚠ How far a ring's BADGES reach, not only its seats: two open rings are kept that far apart,
  // or the names of one lay on the names of the other (review of app#455).
  const reachOf = (id: string, seats: number) => Math.max(seats, (fit.reach.get(id) ?? 0) + 4)
  const spots = new Map<string, Spot>()
  const clusters: Cluster[] = []

  interface Body {
    x: number
    y: number
    /** How far its ring reaches from its middle. */
    reach: number
    place: Place
    hub?: PlannedHub
  }
  const bodies: Body[] = []

  let accountBody: Body | undefined
  if (plan.account) {
    const around = plan.account.around.length
    const ring = ringReach(around, viewerMin) + Math.max(0, ...plan.account.around.map(node => seatOut.get(node.id) ?? 0))
    accountBody = {
      ...project(plan.account.place),
      reach: (around ? reachOf(viewerId, ring + NODE_REACH) : VIEWER_RING_MIN_RADIUS / 2) * scaleOf(viewerId),
      place: plan.account.place,
    }
    bodies.push(accountBody)
  }
  plan.hubs.forEach(hub => {
    const ring = ringReach(hub.nodes.length, hubMin(hub.id)) + Math.max(0, ...hub.nodes.map(id => seatOut.get(id) ?? 0))
    bodies.push({
      ...project(hub.place),
      reach: (hub.nodes.length ? reachOf(hub.id, ring + NODE_REACH) : NODE_REACH) * scaleOf(hub.id),
      place: hub.place,
      hub,
    })
  })

  // Group the bodies whose rings would touch.
  const group = bodies.map((_, i) => i)
  const find = (i: number): number => (group[i] === i ? i : (group[i] = find(group[i])))
  bodies.forEach((a, i) =>
    bodies.forEach((b, j) => {
      if (i < j && Math.hypot(a.x - b.x, a.y - b.y) < a.reach + b.reach + RING_GAP) group[find(i)] = find(j)
    })
  )
  const groups = new Map<number, Body[]>()
  bodies.forEach((body, i) => groups.set(find(i), [...(groups.get(find(i)) ?? []), body]))

  const middleOf = new Map<Body, { x: number; y: number }>()
  groups.forEach(group => {
    const hubs = group.filter(body => body.hub)
    const account = group.find(body => !body.hub)
    if (group.length === 1) {
      middleOf.set(group[0], { x: group[0].x, y: group[0].y })
      return
    }
    // The user icon keeps its place and the locations go round it. With no icon the locations
    // go round the middle of where they are.
    const centre = account ?? {
      x: hubs.reduce((sum, body) => sum + body.x, 0) / hubs.length,
      y: hubs.reduce((sum, body) => sum + body.y, 0) / hubs.length,
    }
    if (account) middleOf.set(account, { x: account.x, y: account.y })
    const widest = Math.max(...hubs.map(body => body.reach))
    const apart = hubs.length > 1 ? (widest + RING_GAP / 2) / Math.sin(Math.PI / hubs.length) : 0
    const clear = account ? account.reach + widest + RING_GAP : 0
    // Each location on the side it really is. Locations on one point have no side: by id.
    const onCentre = (body: Body) => Math.hypot(body.x - centre.x, body.y - centre.y) <= 0.5
    const bearing = (body: Body) => (onCentre(body) ? -Math.PI / 2 : Math.atan2(body.y - centre.y, body.x - centre.x))
    const ordered = [...hubs].sort((a, b) => bearing(a) - bearing(b) || byId(a.hub!, b.hub!))

    // Round the user icon, at the distance that clears it, each location keeps its REAL bearing
    // and moves along the ring only as far as it must to clear its neighbour. Two locations on
    // one point (a city in two networks) then sit side by side, on the side that city is.
    const step = clear ? 2 * Math.asin(Math.min(1, (widest + RING_GAP / 2) / clear)) : 0
    if (account && ordered.length * step < 2 * Math.PI) {
      // ⚠⚠ THE BEARINGS ARE ON A CIRCLE, AND THE LIST IS A LINE. `ordered` goes from -π to π, so
      // two locations on the two sides of due WEST (-3.1 and 3.1) are the first and the last of
      // it, and the pass below, which looks at neighbours in the list, did not see that they are
      // 0.08 apart: it drew them 18 px apart where the same pair to the east got 110. So the list
      // is cut at the WIDEST gap between two bearings, where no two neighbours are close, and the
      // bearings before the cut go one turn up.
      const turns = ordered.map(bearing)
      let cut = 0
      let widestGap = -Infinity
      turns.forEach((turn, i) => {
        const next = i + 1 < turns.length ? turns[i + 1] : turns[0] + 2 * Math.PI
        if (next - turn > widestGap) {
          widestGap = next - turn
          cut = (i + 1) % turns.length
        }
      })
      let runs = [...ordered.slice(cut), ...ordered.slice(0, cut)].map((body, i) => ({
        mean: bearing(body) + (i >= ordered.length - cut ? 2 * Math.PI : 0),
        bodies: [body],
      }))
      for (let i = 0; i < runs.length - 1; ) {
        const [left, right] = [runs[i], runs[i + 1]]
        const count = left.bodies.length + right.bodies.length
        if (right.mean - left.mean >= (count / 2) * step) {
          i++
          continue
        }
        const mean = (left.mean * left.bodies.length + right.mean * right.bodies.length) / count
        runs = [...runs.slice(0, i), { mean, bodies: [...left.bodies, ...right.bodies] }, ...runs.slice(i + 2)]
        i = Math.max(0, i - 1)
      }
      runs.forEach(run =>
        run.bodies.forEach((body, i) => {
          const angle = run.mean + (i - (run.bodies.length - 1) / 2) * step
          middleOf.set(body, { x: centre.x + clear * Math.cos(angle), y: centre.y + clear * Math.sin(angle) })
        })
      )
      return
    }

    // With no user icon, or with no room for that, evenly round the middle, from the first
    // location's own bearing.
    const offsets = ringOffsets(ordered.length, Math.max(apart, clear), bearing(ordered[0]))
    ordered.forEach((body, i) => middleOf.set(body, { x: centre.x + offsets[i].dx, y: centre.y + offsets[i].dy }))
  })

  // Too busy? See the note above. A group is busy when drawing it open MOVES a place, or when it
  // covers too much ground and holds more than `CLUSTER_MIN_NODES` nodes.
  //
  // ⚠ The move is measured for each PLACE, not each location: two locations on one point (a city
  // in two networks) are side by side round it by design, and the middle of the two is still on
  // the place. Measured for each location, that city would close every location at every zoom.
  const busyGroup = (group: Body[]): boolean => {
    const hubs = group.filter(body => body.hub)
    const account = group.find(body => !body.hub)
    const around = account && plan.account ? plan.account.around : []
    const count = hubs.reduce((sum, body) => sum + body.hub!.nodes.length, 0) + around.length
    const centre = account ?? {
      x: hubs.reduce((sum, body) => sum + body.x, 0) / hubs.length,
      y: hubs.reduce((sum, body) => sum + body.y, 0) / hubs.length,
    }
    const atPlace = new Map<string, Body[]>()
    hubs.forEach(body => {
      const key = pointKey(body.place)
      atPlace.set(key, [...(atPlace.get(key) ?? []), body])
    })
    const shift = Math.max(
      0,
      ...Array.from(atPlace.values()).map(bodies => {
        const x = bodies.reduce((sum, body) => sum + middleOf.get(body)!.x, 0) / bodies.length
        const y = bodies.reduce((sum, body) => sum + middleOf.get(body)!.y, 0) / bodies.length
        return distanceOf({ x, y }, bodies[0])
      })
    )
    const extent = Math.max(...group.map(body => distanceOf(middleOf.get(body)!, centre) + body.reach))
    const lat = group.reduce((sum, body) => sum + body.place.lat, 0) / group.length
    return (
      shift > CLUSTER_MAX_SHIFT ||
      (count > CLUSTER_MIN_NODES && extent * metersPerPixel(lat, zoom) > CLUSTER_MAX_KM * 1000)
    )
  }
  // ⚠⚠ ONE BUSY GROUP CLOSES EVERY LOCATION (Benoit, 2026-10-02: "in the wide view, all the location
  // nodes should be collapsed with a node count ... likely if one node needs collapsing, all the
  // location nodes should be collapsed"). A wide view is then all icons, and two locations of
  // Amsterdam are not drawn in Norway and in Tunisia while San Francisco is an icon.
  const allBusy = zoom < lastZoom - 0.5 && Array.from(groups.values()).some(busyGroup)

  groups.forEach(group => {
    const hubs = group.filter(body => body.hub)
    const account = group.find(body => !body.hub)
    const around = account && plan.account ? plan.account.around : []
    const busy = allBusy

    if (account && plan.account) {
      const at = middleOf.get(account)!
      spots.set(viewerId, { x: at.x, y: at.y, ...grown(scaleOf(viewerId)) })
    }

    if (busy) {
      // ⚠⚠ ONE ICON FOR EACH LOCATION, AT ITS REAL PLACE, while the icons have room (Benoit,
      // 2026-10-02: "the 2 locations could have been drawn as two separate data centers instead
      // of merging them into one, but as the zoom out some more, they would eventually merge").
      // Icons that would touch are one icon, at the middle of their nodes by count.
      //
      // ⚠⚠ THE USER ICON IS THE ICON OF ITS OWN PLACE (Benoit, 2026-10-02: "the user is the
      // current location data center, no need for another data center node in that case or it
      // does displace the location node"). The nodes that go round it, and a location whose icon
      // would touch it, are counted ON the user icon. The map draws no second icon beside it.
      const parts: { body: Body; weight: number; viewer: boolean }[] = [
        ...hubs.map(body => ({ body, weight: Math.max(1, body.hub!.nodes.length), viewer: false })),
        ...(account ? [{ body: account, weight: around.length, viewer: true }] : []),
      ]
      const reachOf = (part: { viewer: boolean }) => (part.viewer ? VIEWER_RING_MIN_RADIUS / 2 : CLUSTER_REACH)
      const set = parts.map((_, i) => i)
      const root = (i: number): number => (set[i] === i ? i : (set[i] = root(set[i])))
      parts.forEach((a, i) =>
        parts.forEach((b, j) => {
          if (i < j && distanceOf(a.body, b.body) < reachOf(a) + reachOf(b) + CLUSTER_GAP) set[root(i)] = root(j)
        })
      )
      const icons = new Map<number, typeof parts>()
      parts.forEach((part, i) => icons.set(root(i), [...(icons.get(root(i)) ?? []), part]))

      icons.forEach(members => {
        const own = members.filter(part => !part.viewer)
        const viewer = members.some(part => part.viewer)
        const count = own.reduce((sum, part) => sum + part.body.hub!.nodes.length, 0) + (viewer ? around.length : 0)
        // The user icon with no node of its own and no location on it is only the user icon.
        if (!count) return
        const total = members.reduce((sum, part) => sum + part.weight, 0) || 1
        const mean = (pick: (body: Body) => number) =>
          members.reduce((sum, part) => sum + pick(part.body) * part.weight, 0) / total
        const ids = own.map(part => part.body.hub!.id).sort()
        const id = `cluster:${ids.join('+')}${viewer ? '+viewer' : ''}`
        const at = viewer && account ? { x: account.x, y: account.y } : { x: mean(body => body.x), y: mean(body => body.y) }
        if (!viewer) spots.set(id, at)
        clusters.push({
          id,
          ...at,
          place: { lat: mean(body => body.place.lat), lng: mean(body => body.place.lng) },
          hubs: ids,
          nodes: [...own.flatMap(part => part.body.hub!.nodes), ...(viewer ? around.map(node => node.id) : [])],
          count,
          ...(viewer && plan.account ? { account: plan.account.id } : {}),
        })
      })
      return
    }

    if (account && plan.account && folded.has(plan.account.id) && around.length) {
      // The reader closed the user icon's own ring with a click: the icon carries the count.
      const at = middleOf.get(account)!
      clusters.push({
        id: 'cluster:+viewer',
        x: at.x,
        y: at.y,
        place: plan.account.place,
        hubs: [],
        nodes: around.map(node => node.id),
        count: around.length,
        account: plan.account.id,
        folded: true,
      })
    } else if (account) {
      const at = middleOf.get(account)!
      const seats = ringSeats(around.length, viewerMin)
      const grow = scaleOf(viewerId)
      around.forEach((node, i) => {
        const out = seatOut.get(node.id) ?? 0
        spots.set(node.id, {
          x: at.x + (seats[i].dx + out * Math.cos(seats[i].angle)) * grow,
          y: at.y + (seats[i].dy + out * Math.sin(seats[i].angle)) * grow,
          angle: seats[i].angle,
          ...grown(grow),
          ...(seats[i].inner ? { inner: true } : {}),
          ...(node.atViewer ? { atViewer: true } : {}),
        })
      })
    }
    hubs.forEach(body => {
      const hub = body.hub!
      const at = middleOf.get(body)!
      // ⚠ A location that the reader closed with a click (Benoit, 2026-10-02: "clicking on a
      // location node would toggle the display of the nodes"). It is the icon with its count,
      // where the open location was: nothing round it moves.
      if (folded.has(hub.id) && hub.nodes.length) {
        const id = `cluster:${hub.id}`
        spots.set(id, { x: at.x, y: at.y })
        clusters.push({ id, x: at.x, y: at.y, place: hub.place, hubs: [hub.id], nodes: hub.nodes, count: hub.nodes.length, folded: true })
        return
      }
      const grow = scaleOf(hub.id)
      spots.set(hub.id, { x: at.x, y: at.y, ...grown(grow), ...(hub.atViewer ? { atViewer: true } : {}) })
      const seats = ringSeats(hub.nodes.length, hubMin(hub.id))
      hub.nodes.forEach((id, i) => {
        const out = seatOut.get(id) ?? 0
        spots.set(id, {
          x: at.x + (seats[i].dx + out * Math.cos(seats[i].angle)) * grow,
          y: at.y + (seats[i].dy + out * Math.sin(seats[i].angle)) * grow,
          angle: seats[i].angle,
          ...grown(grow),
          ...(seats[i].inner ? { inner: true } : {}),
        })
      })
    })
  })
  placeFloats(plan, bodies, middleOf, clusters, spots, folded, frame, fit, project, pinned, kept)
  return { spots, clusters }
}

/**
 * The smallest ring round a cloud location: wider than round a location, because the cloud's icon
 * is larger and its name is under it, inside the ring.
 */
export const FLOAT_RING_MIN_RADIUS = 58
/** The window, in screen pixels. */
export interface Frame {
  left: number
  right: number
  top: number
  bottom: number
}
/** How far a cloud location's middle stays in from the edge of the window: its icon and its name. */
const FLOAT_EDGE = 48
/** The space a cloud location's icon takes, from its middle, when it has no open ring. */
const FLOAT_ICON_REACH = 24
/** How many times `placeFloats` moves a cloud location off what it lies on. */
const FLOAT_PASSES = 60

/**
 * Where each cloud location goes on the screen (`PlannedFloat`), with its nodes on a ring round it.
 *
 * ⚠⚠ IT FLOATS, AND IT ARRANGES ITSELF (Benoit, 2026-10-20: "free in the layout"). It is not on the
 * earth, so nothing pins it to a place. It goes to the MIDDLE of the drawn locations that share a
 * network with it (`near`), and from there it moves the shortest way off every ring and every icon
 * it lies on, and off the cloud locations placed before it. It moves with the map because what it is
 * near does, and it never lies on a real place.
 *
 * - Nothing near (its networks have no other drawn location): the middle of everything that is drawn,
 *   and with nothing drawn, the middle of the window.
 * - On the exact middle of what it must leave (one location near it), it leaves to the EAST for the
 *   first cloud and to the WEST for the second, so the two are on two sides of the fleet. Off
 *   another cloud it moves sideways, to that same side.
 * - It stays in the window (`frame`): its whole ring when there is room, and at least its icon.
 * - ⚠⚠ IT STAYS WHERE IT IS UNTIL IT LEAVES THE WINDOW (Benoit, 2026-10-20: "when I pan the map in
 *   the app, the network node moves all over the place. there is no reason for anything to move
 *   until floating locations are getting out of the screen"). Where it was drawn last (`kept`) is a
 *   place on the earth, so it moves with the map; it stays there while its ring is in the window
 *   and clear of everything. Only then does it move: from there, held in the window, and off what
 *   it lies on. The defect: its middle was worked out again at each move of the map, and that middle
 *   is far off the window when one of its networks is on another continent. So it was held at the
 *   edge, in the window, and moved with each pan, and the networks that link to it moved with it.
 * - ⚠ A cloud with a point (`pinned`: an Owner or Admin put it there, nobgp-router#1976) starts at
 *   that point, like a location at its place: it moves with the map and is not held in the window.
 *   It moves off a location it lies on, as little as it must, and never off another cloud: the
 *   clouds placed after it keep clear of it.
 * - ⚠ It stays open at every zoom, and a busy view does not close it (`layoutView`): it covers no
 *   ground. A click closes it (`folded`), and it is then its icon with the count of its nodes.
 * - ⚠ One move per pass, by the overlap, for `FLOAT_PASSES` passes. The place changes as little as
 *   its inputs do, so a zoom moves it smoothly. Rings that leave no room may leave it on one: the
 *   map accepts that, as `layoutView` does for two runs of locations.
 */
function placeFloats(
  plan: GraphPlan,
  bodies: { x: number; y: number; reach: number; hub?: PlannedHub }[],
  middleOf: Map<unknown, { x: number; y: number }>,
  clusters: Cluster[],
  spots: Map<string, Spot>,
  folded: ReadonlySet<string>,
  frame: Frame | undefined,
  fit: RingFits,
  project: (place: Place) => { x: number; y: number },
  pinned: ReadonlyMap<string, Place>,
  kept: ReadonlyMap<string, Place>
) {
  if (!plan.floats.length) return
  // What a float must not lie on: every open ring as it is drawn, and every icon. ⚠ A location or a
  // home icon that is drawn as an icon is that icon, and not its open ring: the ring of the home
  // icon pushed a cloud from California to Greenland in the wide view.
  const iconAt = new Set(clusters.flatMap(cluster => cluster.hubs))
  const homeIsIcon = clusters.some(cluster => cluster.account)
  const solid: { x: number; y: number; reach: number; float?: boolean }[] = [
    ...bodies
      .filter(body => (body.hub ? !iconAt.has(body.hub.id) : !homeIsIcon))
      .map(body => ({ ...(middleOf.get(body) ?? body), reach: body.reach })),
    ...clusters.map(cluster => ({ x: cluster.x, y: cluster.y, reach: cluster.account ? VIEWER_RING_MIN_RADIUS / 2 : CLUSTER_REACH })),
  ]
  // Where each location near a float is drawn: its icon when it is one, its ring's middle when not.
  const drawnAt = new Map<string, { x: number; y: number }>()
  bodies.forEach(body => drawnAt.set(body.hub?.id ?? plan.account?.id ?? '', middleOf.get(body) ?? body))
  clusters.forEach(cluster => {
    cluster.hubs.forEach(id => drawnAt.set(id, cluster))
    if (cluster.account) drawnAt.set(cluster.account, cluster)
  })
  const mean = (points: { x: number; y: number }[]) =>
    points.length
      ? { x: points.reduce((sum, p) => sum + p.x, 0) / points.length, y: points.reduce((sum, p) => sum + p.y, 0) / points.length }
      : null

  // ⚠ The pinned clouds first: each is where it was put, and the others keep clear of it. The side
  // (east, west) is still each cloud's place in `plan.floats`.
  const order = plan.floats.map((float, index) => ({ float, index })).sort((a, b) => Number(pinned.has(b.float.id)) - Number(pinned.has(a.float.id)))
  order.forEach(({ float, index }) => {
    const open = float.nodes.length > 0 && !folded.has(float.id)
    // The ring as `layoutView` draws a location's: as wide as its badges need (`fitRings`).
    const min = Math.max(FLOAT_RING_MIN_RADIUS, fit.min.get(float.id) ?? 0)
    const seats = open ? ringSeats(float.nodes.length, min) : []
    const out = (id: string) => fit.out.get(id) ?? 0
    const ring = ringReach(float.nodes.length, min) + Math.max(0, ...float.nodes.map(out)) + NODE_REACH
    const reach = open ? Math.max(ring, (fit.reach.get(float.id) ?? 0) + 4) : FLOAT_ICON_REACH
    const near = float.near.map(id => drawnAt.get(id)).filter((at): at is { x: number; y: number } => !!at)
    const middle = frame ? { x: (frame.left + frame.right) / 2, y: (frame.top + frame.bottom) / 2 } : { x: 0, y: 0 }
    const start = mean(near) ?? mean(solid) ?? middle
    // West for the second cloud, east for the first and the third.
    const side = index % 2 ? -1 : 1
    const away = side > 0 ? 0 : Math.PI
    // ⚠ In the window, as a network's badge is (`networkGoal`): a cloud that leaves the window leaves
    // its nodes with it, and nothing on the map says where they went. Its whole ring with its names
    // when the window has room, and at least its icon. ⚠ Kept in AT EACH MOVE, not once at the end:
    // once at the end put the cloud back on the home icon it had moved off.
    const roomy = !!frame && frame.right - frame.left > 2 * FLOAT_EDGE && frame.bottom - frame.top > 2 * FLOAT_EDGE
    const edgeX = frame ? Math.max(FLOAT_EDGE, Math.min(reach, (frame.right - frame.left) / 2)) : 0
    const edgeY = frame ? Math.max(FLOAT_EDGE, Math.min(reach, (frame.bottom - frame.top) / 2)) : 0
    const inside = (point: { x: number; y: number }) =>
      roomy && frame
        ? {
            x: Math.min(frame.right - edgeX, Math.max(frame.left + edgeX, point.x)),
            y: Math.min(frame.bottom - edgeY, Math.max(frame.top + edgeY, point.y)),
          }
        : point
    const pin = pinned.get(float.id)
    // ⚠ A pinned cloud starts at its point, and is not held in the window: it is at a place, as a
    // location is. It still moves off a location it lies on, as little as it must: zoomed out, a
    // cloud put over Nevada lay on the icons of California (2026-10-20). Never off another cloud.
    const keep = pin ? (point: { x: number; y: number }) => point : inside
    // Where it was drawn last, when it is still in the window and clear of everything: there, and
    // nothing moves. When it is not, it moves from there, as little as it must.
    const was = !pin && kept.get(float.id) ? project(kept.get(float.id)!) : undefined
    const clear = (point: { x: number; y: number }) =>
      solid.every(other => distanceOf(point, other) >= other.reach + reach + RING_GAP)
    const stays = !!was && distanceOf(inside(was), was) < 0.5 && clear(was)
    let at = pin ? project(pin) : inside(was ?? start)
    for (let pass = 0; !stays && pass < FLOAT_PASSES; pass++) {
      let moved = false
      solid.forEach(other => {
        if (pin && other.float) return
        const need = other.reach + reach + RING_GAP
        const dx = at.x - other.x
        const dy = at.y - other.y
        const apart = Math.hypot(dx, dy)
        if (apart >= need) return
        let out: { x: number; y: number }
        if (other.float) {
          // ⚠ Off another cloud SIDEWAYS, to its own side. Two clouds near the same locations start
          // on almost one point, and the way from one to the other was any way: the second cloud
          // went out of the window.
          out = keep({ x: other.x + side * Math.sqrt(need * need - Math.min(dy * dy, need * need * 0.75)), y: at.y })
        } else {
          const [ux, uy] = apart > 0.5 ? [dx / apart, dy / apart] : [Math.cos(away), Math.sin(away)]
          out = keep({ x: other.x + ux * need, y: other.y + uy * need })
        }
        // Held back by the edge: up or down round what it lies on, along the edge.
        if (Math.hypot(out.x - other.x, out.y - other.y) < need - 0.5) {
          const down = Math.sign(out.y - other.y) || 1
          out = keep({ x: out.x, y: other.y + down * Math.sqrt(Math.max(0, need * need - (out.x - other.x) ** 2)) })
        }
        at = out
        moved = true
      })
      if (!moved) break
    }
    solid.push({ ...at, reach, float: true })

    if (!open) {
      if (!float.nodes.length) return
      const id = `cluster:${float.id}`
      spots.set(id, at)
      clusters.push({
        id,
        ...at,
        place: plan.account?.place ?? { lat: 0, lng: 0 },
        hubs: [float.id],
        nodes: float.nodes,
        count: float.nodes.length,
        folded: true,
        cloudPlace: cloudOf(plan, float.id),
      })
      return
    }
    spots.set(float.id, { ...at })
    float.nodes.forEach((id, i) => {
      const { dx, dy, angle, inner } = seats[i]
      spots.set(id, {
        x: at.x + dx + out(id) * Math.cos(angle),
        y: at.y + dy + out(id) * Math.sin(angle),
        angle,
        ...(inner ? { inner: true } : {}),
      })
    })
  })
}

/** The cloud of a cloud location of the plan. */
function cloudOf(plan: GraphPlan, id: string): CloudPlace | undefined {
  return plan.nodes.find(node => node.id === id)?.cloudPlace
}

/**
 * The zooms at which the map asks what it draws: a quarter apart (`openZoom`, `openZooms`,
 * `openFitZoom`).
 */
const ZOOM_STEP = 0.25

/** The first step further in than `zoom`. */
const nextStep = (zoom: number) => Math.floor(zoom / ZOOM_STEP) * ZOOM_STEP + ZOOM_STEP

/** What the map draws at `zoom`, on the plan's own projection: the view the zoom questions read. */
function viewAt(plan: GraphPlan, zoom: number, lastZoom: number, fit: RingFits, folded: ReadonlySet<string> = new Set()) {
  return layoutView(plan, mercator(zoom), zoom, lastZoom, folded, fit)
}

/** The ids of the locations, and of the user icon, that are data center icons at `zoom`. */
function closedAt(plan: GraphPlan, zoom: number, lastZoom: number, fit: RingFits): Set<string> {
  return new Set(viewAt(plan, zoom, lastZoom, fit).clusters.flatMap(partsOf))
}

/** The locations, and the user icon, that an icon stands for. */
const partsOf = (cluster: Cluster) => [...cluster.hubs, ...(cluster.account ? [cluster.account] : [])]

/**
 * The first zoom, further in than `zoom`, at which the map no longer draws this data center icon:
 * its group is drawn open, or it is two or more smaller groups. A click on the icon goes there.
 *
 * ⚠ The map reads it from `openZooms`, which it keeps (`openZoomOf`). This is the scan that answer
 * is held to (`mapPlaces.test.ts`), and the scan it goes on with when the kept zooms are wrong.
 */
export function openZoom(
  plan: GraphPlan,
  cluster: Cluster,
  zoom: number,
  lastZoom: number,
  fit: RingFits = NO_FIT
): number {
  // ⚠ Until EVERY location of the group is open, not only until this icon is gone (Benoit,
  // 2026-10-03: "one click on a collapsed data center (location) icon should always make the
  // nodes visible"). A group splits into smaller icons on the way in, and the click stopped there.
  const parts = partsOf(cluster)
  for (let next = nextStep(zoom); next < lastZoom; next += ZOOM_STEP) {
    const closed = closedAt(plan, next, lastZoom, fit)
    if (!parts.some(id => closed.has(id))) return next
  }
  return lastZoom
}

/**
 * `openZoom` from the zooms at which each location opens (`openZooms`): the first step further in
 * than `zoom` at which every location of the icon is open. A click on an icon reads the zooms the
 * map keeps, and builds one layout to check the answer.
 *
 * ⚠ The kept zooms are where each location opens FIRST, and the map works them out again only
 * when its places or its ring fits change. A location that closes again further in, or a kept map
 * from before a change of the plan, gives a zoom where the icon is still there, and the click then
 * shows no nodes (Benoit, 2026-10-03: "one click on a collapsed data center (location) icon should
 * always make the nodes visible"). So the answer is checked, and the scan goes on from it.
 */
export function openZoomOf(
  plan: GraphPlan,
  opens: ReadonlyMap<string, number>,
  cluster: Cluster,
  zoom: number,
  lastZoom: number,
  fit: RingFits = NO_FIT
): number {
  const parts = partsOf(cluster)
  const at = Math.max(nextStep(zoom), ...parts.map(id => opens.get(id) ?? lastZoom))
  if (at >= lastZoom) return lastZoom
  const closed = closedAt(plan, at, lastZoom, fit)
  return parts.some(id => closed.has(id)) ? openZoom(plan, cluster, at, lastZoom, fit) : at
}

/**
 * The count that an icon shows: the nodes it stands for, which are the nodes the map would draw
 * there if it were open (Benoit, 2026-10-02: "only one count and it should reflect the number of
 * nodes displayed at that location"). So it follows Show Offline: a node that the toggle hides is
 * not in the plan, and not in the count.
 *
 * ⚠ One number. "11/13", the online count of all of them, was the form before, and it is gone.
 */
export function clusterCount(cluster: Cluster): string {
  return String(cluster.count)
}

/**
 * Whether the map draws a link as a solid line.
 *
 * ⚠⚠ A NETWORK'S LINES ARE ALWAYS DOTTED ON THE MAP (Benoit, 2026-10-09: "I like the dotted line
 * better for the links to the network node"). A network's link to a location is `connected` while
 * a node there has a session, and the graph view draws it solid then. On the map the same network
 * had a solid line to one location and a dotted line to the next.
 *
 * ⚠⚠ A LOCATION'S LINE TO ITS NODE IS ALWAYS SOLID ON THE MAP (Benoit, 2026-10-09: "make all
 * location to node lines solid"). It was solid only while that node had a session, so one node of
 * a ring had a dotted line beside eight solid lines, and nothing on the map said why. The home
 * icon is a location, so its lines to the nodes round it are solid too. ⚠ The line to an offline
 * node is solid too, when Show Offline draws it: the node's own badge says that it is offline.
 *
 * Every other line keeps the graph's rule: a node's line to its service is solid while the
 * service is enabled.
 */
export const solidLink = (link: Pick<GraphLink, 'sourceType' | 'connected'>): boolean => {
  if (link.sourceType === 'network') return false
  if (link.sourceType === 'location') return true
  return !!link.connected
}

/**
 * What the map draws when some groups are data center icons: the plan's graph without the
 * locations and the nodes that the icons stand for, with one node for each icon.
 *
 * - A link INTO a hidden location or node, from something that is still drawn (a network, or the
 *   user icon), goes to the icon. One link for each pair, with the states of all of them.
 * - A link FROM something hidden is not drawn, and a service or a name stays only while a link
 *   that is drawn reaches it.
 * - The icon is a `location` node whose label is its count (`clusterCount`). It is lit when one of its nodes is
 *   lit, selected or streaming, and offline only when all of them are.
 * - A group on the user icon (`Cluster.account`) gets no node: the user icon is drawn as it is,
 *   and the caller puts the count on it. A network's link into the group goes to the user icon.
 *
 * ⚠ `held` is the caller's, and it makes each icon and each link to an icon the SAME OBJECT on
 * every draw, for the reason `planGraph` gives for `merged`.
 */
export function viewGraph(
  plan: GraphPlan,
  clusters: Cluster[],
  held: { nodes: Map<string, GraphNode>; links: Map<string, GraphLink> } = { nodes: new Map(), links: new Map() }
): { nodes: GraphNode[]; links: GraphLink[] } {
  // ⚠⚠ AN OFFLINE NODE SHOWS NO SERVICE ON THE MAP (Benoit, 2026-10-02: "an offline node should
  // not expose and service ... on the map"). Its links to them are not drawn, and a service with
  // no link is not drawn (below).
  const kinds = new Map(plan.nodes.map(node => [node.id, node]))
  const leaf = (id: string) => kinds.get(id)?.type === 'service'
  const ownLinks = plan.links.filter(link => !(kinds.get(endId(link.source))?.offline && leaf(endId(link.target))))
  if (!clusters.length) {
    held.nodes.clear()
    held.links.clear()
    if (ownLinks.length === plan.links.length) return { nodes: plan.nodes, links: plan.links }
    const reachedHere = new Set(ownLinks.map(link => endId(link.target)))
    return { nodes: plan.nodes.filter(node => !leaf(node.id) || reachedHere.has(node.id)), links: ownLinks }
  }
  const iconOf = new Map<string, Cluster>()
  clusters.forEach(cluster => [...cluster.hubs, ...cluster.nodes].forEach(id => iconOf.set(id, cluster)))
  const nodeById = new Map(plan.nodes.map(node => [node.id, node]))

  const links: GraphLink[] = []
  const into = new Map<string, { source: string; cluster: Cluster; sourceType: string; all: GraphLink[] }>()
  // ⚠ A group on the user icon has no node of its own: the user icon is its icon. A link into the
  // group from a network goes to the user icon, and it is ONE line with the network's own link to
  // the user icon (`toUser`), when the network has one.
  const onUser = clusters.find(cluster => cluster.account)
  const toUser: GraphLink[] = []
  ownLinks.forEach(link => {
    const source = endId(link.source)
    const target = endId(link.target)
    if (iconOf.has(source)) return
    const cluster = iconOf.get(target)
    if (!cluster) {
      if (onUser && target === onUser.account) toUser.push(link)
      else links.push(link)
      return
    }
    // The user icon's links to its own nodes are not drawn while they are counted on it.
    if (source === cluster.account) return
    const id = `${source}>${cluster.id}`
    const entry = into.get(id) ?? { source, cluster, sourceType: link.sourceType, all: [] }
    entry.all.push(link)
    into.set(id, entry)
  })
  toUser.forEach(link => {
    const entry = onUser && into.get(`${endId(link.source)}>${onUser.id}`)
    if (entry) entry.all.push(link)
    else links.push(link)
  })
  into.forEach((entry, id) => {
    const fields = {
      connected: entry.all.some(link => link.connected),
      streaming: entry.all.some(link => link.streaming),
      active: entry.all.some(link => link.active),
      hidden: entry.all.every(link => link.hidden),
      rxBytes: entry.all.reduce((sum, link) => sum + (link.rxBytes ?? 0), 0),
      txBytes: entry.all.reduce((sum, link) => sum + (link.txBytes ?? 0), 0),
      hue: entry.all.find(link => link.hue !== undefined)?.hue,
    }
    const existing = held.links.get(id)
    if (existing) {
      const changed = (Object.keys(fields) as (keyof typeof fields)[]).some(k => existing[k] !== fields[k])
      Object.assign(existing, fields, { stale: changed })
      links.push(existing)
      return
    }
    const created: GraphLink = {
      id,
      source: entry.source,
      target: entry.cluster.account ?? entry.cluster.id,
      sourceType: entry.sourceType,
      targetType: 'node',
      stale: false,
      ...fields,
    }
    held.links.set(id, created)
    links.push(created)
  })
  Array.from(held.links.keys()).forEach(id => {
    if (!into.has(id)) held.links.delete(id)
  })

  const reached = new Set(links.map(link => endId(link.target)))
  const nodes = plan.nodes.filter(node => {
    if (iconOf.has(node.id)) return false
    if (node.type === 'service') return reached.has(node.id)
    return true
  })
  clusters.forEach(cluster => {
    if (cluster.account) return
    const members = cluster.nodes.map(id => nodeById.get(id)).filter((node): node is GraphNode => !!node)
    const fields = {
      label: clusterCount(cluster),
      active: members.some(node => node.active),
      selected: members.some(node => node.selected),
      streaming: members.some(node => node.streaming),
      connected: members.some(node => node.connected),
      offline: members.length > 0 && members.every(node => node.offline),
      // A closed icon has no node glyphs, so it takes the colors of the networks it holds (app#498).
      arcs: mergeArcs(members.map(node => node.arcs)),
    }
    const existing = held.nodes.get(cluster.id)
    if (existing) {
      const changed = (Object.keys(fields) as (keyof typeof fields)[]).some(k => existing[k] !== fields[k])
      Object.assign(existing, fields, { stale: changed })
      nodes.push(existing)
      return
    }
    const created = { id: cluster.id, type: 'location', ...(cluster.cloudPlace ? { cloudPlace: cluster.cloudPlace } : {}), ...fields } as GraphNode
    held.nodes.set(cluster.id, created)
    nodes.push(created)
  })
  const ids = new Set(clusters.filter(cluster => !cluster.account).map(cluster => cluster.id))
  Array.from(held.nodes.keys()).forEach(id => {
    if (!ids.has(id)) held.nodes.delete(id)
  })
  return { nodes, links }
}

/** Every place the plan pins something at, for `boundsOf`. ⚠ Not a cloud location's: it has none. */
export function planPlaces(plan: GraphPlan): Place[] {
  return [...(plan.account ? [plan.account.place] : []), ...plan.hubs.map(hub => hub.place)]
}

/**
 * The plan's places as one string, the same for the same set of places in any order.
 *
 * The map is fitted to its places. When this changes and the reader has not moved the map, the
 * map is fitted again (`mapCore.ts`): a switch to an org on another continent must not leave the
 * map on the places of the org before, with nothing on it.
 */
export function placesKey(plan: GraphPlan): string {
  return Array.from(new Set(planPlaces(plan).map(pointKey)))
    .sort()
    .join(' ')
}

/**
 * The box that holds every place, as `[[west, south], [east, north]]`, or null with no place.
 *
 * ⚠ A plain minimum and maximum. A fleet on both sides of the 180th meridian (Fiji and Hawaii)
 * gets a box the long way round the world. That is a wide view, not a wrong one.
 */
export function boundsOf(places: Place[]): [[number, number], [number, number]] | null {
  if (!places.length) return null
  let west = Infinity
  let south = Infinity
  let east = -Infinity
  let north = -Infinity
  places.forEach(place => {
    west = Math.min(west, place.lng)
    east = Math.max(east, place.lng)
    south = Math.min(south, place.lat)
    north = Math.max(north, place.lat)
  })
  return [
    [west, south],
    [east, north],
  ]
}

/** A box round a drawn thing, from the middle of the node it belongs to. */
export interface Box {
  x: number
  y: number
  width: number
  height: number
}

/**
 * How far to move the box `a`, drawn at `at`, so that it does not lie on the box `b`, drawn at
 * `bt`: along the axis that needs the shorter move, and away from `b`. Null when they do not touch.
 *
 * ⚠ The map keeps a service or a name, with its own label under it, off the name chip of every
 * node (Benoit, 2026-10-03: "the service names overlap with the node name"). The collide force
 * cannot do it: it knows circles round the glyphs, and a name is a long box beside its node.
 */
export function clearOf(
  a: Box,
  at: { x: number; y: number },
  b: Box,
  bt: { x: number; y: number }
): { dx: number; dy: number } | null {
  const left = at.x + a.x
  const top = at.y + a.y
  const otherLeft = bt.x + b.x
  const otherTop = bt.y + b.y
  const across = Math.min(left + a.width - otherLeft, otherLeft + b.width - left)
  const down = Math.min(top + a.height - otherTop, otherTop + b.height - top)
  if (across <= 0 || down <= 0) return null
  if (across <= down) {
    return { dx: left + a.width / 2 < otherLeft + b.width / 2 ? -across : across, dy: 0 }
  }
  return { dx: 0, dy: top + a.height / 2 < otherTop + b.height / 2 ? -down : down }
}

/** A box on the screen: its four edges. */
export interface Rect {
  left: number
  top: number
  right: number
  bottom: number
}

/** The box `box`, drawn at `at`, on the screen, with `gap` more on each side. */
export function rectAt(box: Box, at: { x: number; y: number }, gap = 0): Rect {
  return { left: at.x + box.x - gap, top: at.y + box.y - gap, right: at.x + box.x + box.width + gap, bottom: at.y + box.y + box.height + gap }
}

/** How much of `a` lies on `b`, in square pixels. */
export function overlapArea(a: Rect, b: Rect): number {
  const across = Math.min(a.right, b.right) - Math.max(a.left, b.left)
  const down = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top)
  return across > 0 && down > 0 ? across * down : 0
}

/**
 * The first spot round `centre` where `box` lies on nothing in `taken`: at each distance in
 * `radii`, from the bearing `prefer` and turning out to both sides in `steps` steps. Where no spot
 * is free, the one that lies on the least.
 *
 * ⚠ A service or a name that a hover shows is placed with this, not by the simulation (Benoit,
 * 2026-10-03: "the service name should position itself close to the node, but not necessarily
 * tied below"). It is placed once, where there is room, and it stays there: the simulation moved
 * it while the reader went to it, and could leave it in a ring with no way out.
 */
export function findRoom(
  box: Box,
  centre: { x: number; y: number },
  prefer: number,
  taken: Rect[],
  radii: number[],
  steps = 16,
  clockwise = false,
  /** The part of a turn to try at each distance before the next one, from `prefer` clockwise. */
  span = 1
): { x: number; y: number } {
  let best = { x: centre.x + radii[0] * Math.cos(prefer), y: centre.y + radii[0] * Math.sin(prefer) }
  let least = Infinity
  for (const radius of radii) {
    for (let k = 0; k < Math.max(1, Math.round(steps * span)); k++) {
      // Out to both sides by turns, or clockwise only, for things placed in order round one middle.
      const turn = clockwise ? k * ((2 * Math.PI) / steps) : (k % 2 ? 1 : -1) * Math.ceil(k / 2) * ((2 * Math.PI) / steps)
      const at = { x: centre.x + radius * Math.cos(prefer + turn), y: centre.y + radius * Math.sin(prefer + turn) }
      const rect = rectAt(box, at)
      const covered = taken.reduce((sum, other) => sum + overlapArea(rect, other), 0)
      if (covered === 0) return at
      if (covered < least) {
        least = covered
        best = at
      }
    }
  }
  return best
}

/** Where a label goes round its glyph: its side, and what SVG needs to draw it there. */
export interface LabelSpot {
  side: 'right' | 'left' | 'above' | 'below' | 'above-right' | 'above-left' | 'below-right' | 'below-left'
  /** The point the text is anchored at, from the middle of its node. */
  x: number
  y: number
  anchor: 'start' | 'middle' | 'end'
  /** The box of the label (with its chip, if it has one), from the middle of its node. */
  box: Box
}

/**
 * The places round a glyph where a label of `width` by `height` can go, each `gap` clear of the
 * glyph, with `pad` round the text for a chip. The text's middle line is at the anchor point.
 */
export function labelSpots(glyph: Box, width: number, height: number, gap: number, pad: { x: number; y: number }): LabelSpot[] {
  const middle = { x: glyph.x + glyph.width / 2, y: glyph.y + glyph.height / 2 }
  const w = width + 2 * pad.x
  const h = height + 2 * pad.y
  const right = glyph.x + glyph.width + gap
  const left = glyph.x - gap
  const above = glyph.y - gap - h / 2
  const below = glyph.y + glyph.height + gap + h / 2
  // A corner label starts at the middle of the glyph's side, so it stays close to it.
  const spot = (side: LabelSpot['side'], x: number, y: number, anchor: LabelSpot['anchor']): LabelSpot => {
    const boxLeft = anchor === 'start' ? x : anchor === 'end' ? x - w : x - w / 2
    const textX = anchor === 'start' ? x + pad.x : anchor === 'end' ? x - pad.x : x
    return { side, x: textX, y, anchor, box: { x: boxLeft, y: y - h / 2, width: w, height: h } }
  }
  return [
    spot('right', right, middle.y, 'start'),
    spot('left', left, middle.y, 'end'),
    spot('above', middle.x, above, 'middle'),
    spot('below', middle.x, below, 'middle'),
    spot('above-right', middle.x, above, 'start'),
    spot('above-left', middle.x, above, 'end'),
    spot('below-right', middle.x, below, 'start'),
    spot('below-left', middle.x, below, 'end'),
  ]
}


/**
 * A service's name without its node's name in front (Benoit, 2026-10-03: "do not repeat the node
 * name in the service name"). On the map a service's badge hangs off its node, so "sparky-direct
 * Terminal" reads "Terminal". Only a whole name followed by a space or a separator goes: a node
 * "pi" leaves "pi5 web" as it is. A name that would be left empty is kept.
 */
export function withoutNodeName(name: string, node: string | undefined): string {
  if (!node || !name.toLowerCase().startsWith(node.toLowerCase())) return name
  const rest = name.slice(node.length)
  if (!/^[\s\-–—:·|/]/.test(rest)) return name
  return rest.replace(/^[\s\-–—:·|/]+/, '') || name
}

/**
 * The seats of a ring on which the badges of its nodes lie on nothing: not on each other and not
 * on the icon in the middle. Each badge reaches away from its node (`boxes`, in seat order, from
 * the middle of each node).
 *
 * ⚠ THE RING KEEPS ITS SIZE (Benoit, 2026-10-03: "keep them round", then "still too wide"). Where
 * two badges meet, which is near 12 and 6 o'clock, where two seats are almost level, the seat
 * nearer the vertical moves OUT from the middle (`extra`, a few pixels each) until they part. Only
 * when that is not enough (more than `MAX_EXTRA` out) does the whole ring grow.
 *
 * ⚠ One ring only. A location with rings inside each other keeps `min`: its inner names show only
 * when lit.
 */
export function ringFit(boxes: Box[], min: number, middle = 16, gap = 3): { radius: number; extra: number[]; reach: number } {
  const count = boxes.length
  // How far from the middle the furthest corner of a badge is.
  const reach = (rects: Rect[]) =>
    Math.max(0, ...rects.map(r => Math.hypot(Math.max(Math.abs(r.left), Math.abs(r.right)), Math.max(Math.abs(r.top), Math.abs(r.bottom)))))
  const none = { radius: min, extra: boxes.map(() => 0), reach: 0 }
  if (count === 0 || count > RING_SINGLE_MAX) return none
  const centre: Rect = { left: -middle, top: -middle, right: middle, bottom: middle }
  for (let radius = ringRadius(count, min); radius <= 640; radius += 4) {
    const seats = ringOffsets(count, radius)
    const extra = seats.map(() => 0)
    const rectOf = (i: number) =>
      rectAt(boxes[i], { x: seats[i].dx + extra[i] * Math.cos(seats[i].angle), y: seats[i].dy + extra[i] * Math.sin(seats[i].angle) }, gap / 2)
    for (let pass = 0; pass < 200; pass++) {
      let moved = false
      for (let i = 0; i < count && !moved; i++) {
        for (let j = i + 1; j < count && !moved; j++) {
          if (!overlapArea(rectOf(i), rectOf(j))) continue
          // The seat nearer the vertical moves out: its neighbour is almost level with it.
          const k = Math.abs(Math.cos(seats[i].angle)) <= Math.abs(Math.cos(seats[j].angle)) ? i : j
          extra[k] += 2
          moved = true
        }
      }
      if (!moved) break
    }
    const rects = seats.map((_, i) => rectOf(i))
    const clear =
      extra.every(out => out <= MAX_EXTRA) &&
      rects.every((rect, i) => !overlapArea(rect, centre) && rects.every((other, j) => j <= i || !overlapArea(rect, other)))
    if (clear) return { radius, extra, reach: reach(rects) }
  }
  return { radius: 640, extra: boxes.map(() => 0), reach: 640 }
}

/**
 * What `ringFit` worked out for each ring, which `layoutView` lays the rings out with: the least
 * radius of each ring and how far its badges reach (by the id of its location, or of the user
 * icon), and how far each seat is out from its ring (by node id).
 */
export interface RingFits {
  min: ReadonlyMap<string, number>
  out: ReadonlyMap<string, number>
  reach: ReadonlyMap<string, number>
}
export const NO_FIT: RingFits = { min: new Map(), out: new Map(), reach: new Map() }

/** The most a seat moves out from its ring to part two badges (`ringFit`). Past it the ring grows. */
export const MAX_EXTRA = 28

/**
 * Which side of its dot a node's name is on, from the node's bearing round its ring: away from the
 * middle, so the dot is the end of the badge nearer the data center (Benoit, 2026-10-03, "dot
 * toward the center"). ⚠ Straight above the middle, on the RIGHT, and straight below, on the LEFT
 * (Benoit, 2026-10-03: "since we go clockwise, the [side] for the 12 o'clock position must be right
 * and for the 6 o'clock it should be left"): the way the ring goes on from there. Where that badge
 * meets its neighbour's, `ringFit` moves its seat out.
 */
export function sideOf(angle: number): 'right' | 'left' {
  const across = Math.cos(angle)
  if (Math.abs(across) <= 1e-6) return Math.sin(angle) < 0 ? 'right' : 'left'
  return across > 0 ? 'right' : 'left'
}

/**
 * The lowest zoom at which each location's group is drawn open, by the id of its location or of the
 * user icon, in steps of a quarter. A group that is open from the widest zoom has 0.
 */
export function openZooms(plan: GraphPlan, lastZoom: number, fit: RingFits = NO_FIT): Map<string, number> {
  const ids = [...plan.hubs.map(hub => hub.id), ...(plan.account ? [plan.account.id] : [])]
  const open = new Map<string, number>()
  for (let zoom = 0; zoom <= lastZoom && open.size < ids.length; zoom += ZOOM_STEP) {
    const closed = closedAt(plan, zoom, lastZoom, fit)
    ids.forEach(id => {
      if (!open.has(id) && !closed.has(id)) open.set(id, zoom)
    })
  }
  return open
}

/**
 * How much larger a group is drawn at `zoom` (Benoit, 2026-10-03: "as we zoom further, we should
 * scale the nodes and spread them to maintain the same overall cluster, but bigger on the screen up
 * to a max ... no more than twice the size"): as the map grows, from the zoom at which the group
 * is first seen open (`from`), up to `max`. Below that zoom, the size it has today.
 *
 * ⚠ At the same rate as the map, so the group keeps its shape against it: which groups are busy
 * at a zoom does not change with it.
 */
export function groupScale(zoom: number, from: number, max = 2): number {
  return Math.min(max, Math.max(1, 2 ** (zoom - from)))
}

/**
 * The zoom the map opens at: the first one, from `from` in, at which EVERY location is drawn open
 * and all of it is inside a frame `width` by `height`. `from` when there is none.
 *
 * ⚠⚠ EVERY LOCATION IS IN THE FIRST FRAME (Benoit, 2026-10-03), AND OPEN WHEN IT CAN BE. The fit
 * stops at `from`, the scale of a region. A fleet in one metro area (Hayward and Fremont, 65 px
 * apart there) is two rings that touch at that zoom, so it opened as data center icons, though a
 * step or two further in both are open and in the frame. A fleet that cannot be open with all of
 * it in the frame (California and Oregon) opens at `from` or wider, as icons.
 *
 * ⚠ A location the reader closed is an icon at every zoom. It does not count as "not open".
 */
export function openFitZoom(
  plan: GraphPlan,
  width: number,
  height: number,
  from: number,
  lastZoom: number,
  fit: RingFits = NO_FIT,
  folded: ReadonlySet<string> = new Set()
): number {
  for (let zoom = from; zoom <= lastZoom; zoom += ZOOM_STEP) {
    const view = viewAt(plan, zoom, lastZoom, fit, folded)
    const spots = Array.from(view.spots.values())
    if (!spots.length) return from
    const wide = Math.max(...spots.map(spot => spot.x)) - Math.min(...spots.map(spot => spot.x))
    const high = Math.max(...spots.map(spot => spot.y)) - Math.min(...spots.map(spot => spot.y))
    // What is drawn only grows on the way in: past the frame here, it is past it at every nearer zoom.
    if (wide > width || high > height) return from
    if (!view.clusters.some(cluster => !cluster.folded)) return zoom
  }
  return from
}

/**
 * Where a network's badge goes: the middle of the locations it links to, each pulling the same.
 *
 * ⚠⚠ THE BADGE IS IN THE PICTURE WHEN ONE OF ITS LOCATIONS IS (Benoit, 2026-10-03: "if a
 * node/location from a network is visible, we should bring the network node in the picture so we
 * understand what the lines are"). The middle of ALL of them put the badge of a network with one
 * location in Oregon and two in California far out of a window on California: its lines left the
 * window, and nothing said what they were.
 *
 * ⚠⚠ ITS NATURAL PLACE, WHILE THAT IS IN THE WINDOW (Benoit, 2026-10-03: "if the natural position
 * of the network node is visible, there is no reason to bring it to the edge, only when the
 * network node is or moves out of the page and nodes from the network are visible"). The badge
 * went to the edge as soon as ONE location was not in the window, also when the middle of them
 * all was in plain view.
 *
 * ⚠ AT THE EDGE, ON THE SIDE THE OTHERS ARE ("closer to the edge when it is pulled in view", then
 * "the playground node should be toward boardman close to the edge of the map in the direction of
 * the other location"). When the middle of them all is NOT in the window and a location is, the
 * badge goes from the middle of the locations in the window in the direction of the middle of the
 * others, AS FAR AS `edge`. Its lines then point to the locations that are not in the window.
 * With no location in the window, the middle of them all.
 *
 * ⚠ "In the window" is in `edge` here, not in `view`: a natural place in the strip that `edge`
 * keeps for the name put the name out of the window, and the badge jumped the width of that strip
 * when its natural place went past the right side of the window. With every location in the
 * window and their middle in that strip, the badge stops at `edge` for the same reason.
 */
export function networkGoal(
  anchors: { x: number; y: number }[],
  view: { left: number; right: number; top: number; bottom: number },
  /** Where a badge that is pulled in stops: inside `view`, with room for its name. */
  edge = view
): { x: number; y: number } | null {
  if (!anchors.length) return null
  const middle = (on: { x: number; y: number }[]) => ({
    x: on.reduce((sum, a) => sum + a.x, 0) / on.length,
    y: on.reduce((sum, a) => sum + a.y, 0) / on.length,
  })
  const inside = (a: { x: number; y: number }, box: typeof view) => a.x >= box.left && a.x <= box.right && a.y >= box.top && a.y <= box.bottom
  const seen = anchors.filter(a => inside(a, view))
  const others = anchors.filter(a => !inside(a, view))
  const natural = middle(anchors)
  if (!seen.length || inside(natural, edge)) return natural
  const within = (at: { x: number; y: number }) => ({
    x: Math.min(edge.right, Math.max(edge.left, at.x)),
    y: Math.min(edge.bottom, Math.max(edge.top, at.y)),
  })
  // Every location is in the window, and the middle of them is in the strip kept for the name.
  if (!others.length) return within(natural)
  // From the middle of what is in the window toward the middle of the others, as far as `edge`.
  const from = middle(seen)
  const all = middle(others)
  const start = within(from)
  const dx = all.x - start.x
  const dy = all.y - start.y
  const tx = dx > 0 ? (edge.right - start.x) / dx : dx < 0 ? (edge.left - start.x) / dx : Infinity
  const ty = dy > 0 ? (edge.bottom - start.y) / dy : dy < 0 ? (edge.top - start.y) / dy : Infinity
  // ⚠ There is always a direction: the middle of the others is out of `edge` here, and `start` is in it.
  const t = Math.min(tx, ty)
  return { x: start.x + dx * t, y: start.y + dy * t }
}

/**
 * The links whose two ends are drawn, each end the drawn NODE (Benoit, 2026-10-03: "there is no
 * line between the node and the service"). The adapter gives an end as an id; d3's link force
 * turned ids into nodes until app#455 removed it, and then no line had ends. An end already
 * resolved on an earlier draw is looked up again by its id: a node that is no longer drawn (it is
 * in a data center icon now) takes its links with it.
 *
 * ⚠ It writes the nodes into the link, as d3 did: the same link objects are drawn on every tick.
 */
export function resolveLinks(nodes: GraphNode[], links: GraphLink[]): GraphLink[] {
  const byId = new Map(nodes.map(node => [node.id, node]))
  return links.filter(link => {
    const source = byId.get(endId(link.source))
    const target = byId.get(endId(link.target))
    if (!source || !target) return false
    link.source = source
    link.target = target
    return true
  })
}
